<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import EvolutionInstancesAPI from 'dashboard/api/evolutionInstances';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const POLL_INTERVAL = 3000;

const router = useRouter();
const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const inboxName = ref('');
const inboxId = ref(null);
const qrCode = ref('');
const pairingCode = ref('');
const status = ref('');
const isCreating = ref(false);
const isRefreshing = ref(false);
const isCancelling = ref(false);
const completed = ref(false);
let pollingTimer = null;

const hasConnection = computed(() => Boolean(inboxId.value));
const canCreate = computed(
  () => inboxName.value.trim().length > 0 && !isCreating.value
);

const errorMessage = (error, fallbackMessage) =>
  error.response?.data?.message || error.message || fallbackMessage;

const stopPolling = () => {
  if (pollingTimer) {
    window.clearInterval(pollingTimer);
    pollingTimer = null;
  }
};

const finishConnection = async () => {
  if (completed.value) return;

  completed.value = true;
  stopPolling();
  if (
    currentAccount.value?.custom_attributes?.yeszap_onboarding_status ===
    'pending'
  ) {
    try {
      await updateAccount(
        { yeszap_onboarding_status: 'conversations_pending' },
        { silent: true }
      );
    } catch {
      // The WhatsApp connection is valid even if onboarding state persistence fails.
    }
  }
  useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONNECTED'));
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: inboxId.value },
  });
};

const checkStatus = async () => {
  if (!inboxId.value || completed.value) return;

  try {
    const { data } = await EvolutionInstancesAPI.status(inboxId.value);
    status.value = data.status;
    if (data.connected) await finishConnection();
  } catch {
    stopPolling();
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.STATUS_ERROR'));
  }
};

const startPolling = () => {
  stopPolling();
  pollingTimer = window.setInterval(checkStatus, POLL_INTERVAL);
};

const updateConnectionData = async data => {
  inboxId.value = data.inbox_id || inboxId.value;
  qrCode.value = data.qr_code || '';
  pairingCode.value = data.pairing_code || '';
  status.value = data.status || 'connecting';

  if (data.connected) {
    await finishConnection();
  } else {
    startPolling();
  }
};

const refreshQrCode = async () => {
  if (!inboxId.value) return;

  isRefreshing.value = true;
  try {
    const { data } = await EvolutionInstancesAPI.connect(inboxId.value);
    await updateConnectionData(data);
  } catch (error) {
    useAlert(
      errorMessage(error, t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR_ERROR'))
    );
  } finally {
    isRefreshing.value = false;
  }
};

const createConnection = async () => {
  if (!canCreate.value) return;

  isCreating.value = true;
  try {
    const { data } = await EvolutionInstancesAPI.create({
      inbox_name: inboxName.value.trim(),
    });
    await updateConnectionData(data);
    if (!data.qr_code && !data.connected) await refreshQrCode();
  } catch (error) {
    useAlert(
      errorMessage(error, t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CREATE_ERROR'))
    );
  } finally {
    isCreating.value = false;
  }
};

const cancel = async () => {
  stopPolling();
  isCancelling.value = true;

  try {
    if (inboxId.value) await EvolutionInstancesAPI.delete(inboxId.value);
    router.replace({ query: {} });
  } catch (error) {
    useAlert(
      errorMessage(error, t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DELETE_ERROR'))
    );
  } finally {
    isCancelling.value = false;
  }
};

onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="mx-auto flex w-full max-w-lg flex-col gap-6">
    <div class="flex items-start gap-3">
      <div
        class="flex size-11 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2"
      >
        <Icon icon="i-lucide-qr-code" class="size-6 text-n-slate-10" />
      </div>
      <div>
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.TITLE') }}
        </h3>
        <p class="mt-1 text-sm leading-6 text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div class="flex items-start gap-3 rounded-lg bg-n-alpha-2 p-4">
      <Icon
        icon="i-lucide-download"
        class="mt-0.5 size-5 shrink-0 text-n-slate-10"
      />
      <p class="m-0 text-sm leading-6 text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.IMPORT_NOTICE') }}
      </p>
    </div>

    <form
      v-if="!hasConnection"
      class="flex flex-col gap-5"
      @submit.prevent="createConnection"
    >
      <label class="flex flex-col gap-2 text-sm font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.YESZAP.CHANNEL_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          maxlength="100"
          class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="$t('INBOX_MGMT.YESZAP.CHANNEL_NAME.PLACEHOLDER')"
        />
      </label>

      <div class="flex justify-end gap-2">
        <NextButton
          type="button"
          slate
          outline
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CANCEL')"
          @click="cancel"
        />
        <NextButton
          type="submit"
          solid
          blue
          icon="i-lucide-qr-code"
          :disabled="!canCreate"
          :is-loading="isCreating"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.GENERATE_QR')"
        />
      </div>
    </form>

    <div v-else class="flex flex-col items-center gap-5 text-center">
      <div class="rounded-lg bg-white p-3">
        <img
          v-if="qrCode"
          :src="qrCode"
          :alt="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR_ALT')"
          class="size-64 max-h-[70vw] max-w-[70vw]"
        />
        <div
          v-else
          class="flex size-64 max-h-[70vw] max-w-[70vw] items-center justify-center text-n-slate-8"
        >
          <Icon icon="i-lucide-loader-circle" class="size-8 animate-spin" />
        </div>
      </div>

      <div>
        <p class="font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_DESCRIPTION') }}
        </p>
        <p v-if="pairingCode" class="mt-3 font-mono text-lg text-n-slate-12">
          {{ pairingCode }}
        </p>
      </div>

      <div class="flex flex-wrap justify-center gap-2">
        <NextButton
          slate
          outline
          :is-loading="isCancelling"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CANCEL')"
          @click="cancel"
        />
        <NextButton
          solid
          blue
          icon="i-lucide-refresh-cw"
          :is-loading="isRefreshing"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.REFRESH_QR')"
          @click="refreshQrCode"
        />
      </div>
    </div>
  </div>
</template>
